import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import useRazorpay from "react-razorpay";
import config from "../../config";
import Footer from "./Footer";
import Header from "./Header";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000, 50000];

const MIN_RECHARGE_AMOUNT = 500;
const MAX_RECHARGE_AMOUNT = 5000000;

const Recharge_wallet = () => {
  // =========================================================
  // USER
  // =========================================================
  const userDetail = localStorage.getItem("ship_rocket_user");

  let userData = {};

  try {
    if (userDetail) {
      userData = JSON.parse(userDetail);
    }
  } catch (error) {
    console.error("Unable to parse user data:", error);
  }

  // =========================================================
  // RAZORPAY
  // =========================================================
  const [Razorpay, isLoaded] = useRazorpay();

  // =========================================================
  // STATE
  // =========================================================
  const [walletBalance, setWalletBalance] = useState(0);

  // Always keep rechargeAmount as NUMBER
  const [rechargeAmount, setRechargeAmount] = useState(500);

  // Explicitly track selected quick amount
  const [selectedQuickAmount, setSelectedQuickAmount] = useState(500);

  const [paymentGateways, setPaymentGateways] = useState([]);

  // Always keep gateway ID as STRING
  const [selectedGatewayId, setSelectedGatewayId] = useState("");

  const [loading, setLoading] = useState(false);

  // =========================================================
  // FETCH WALLET BALANCE / PAYMENT GATEWAYS
  // =========================================================
  useEffect(() => {
    getWalletBalance();
  }, []);

  const getWalletBalance = async () => {
    try {
      const dataToSend = {
        customer_id: userData.customer_id,
      };

      const url = config.apiUrl + "/user/get_wallet_balance";

      const res = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (res.data.status) {
        const balance = Number(
          res.data.find_customer_wallet?.[0]?.wallet_balance || 0
        );

        setWalletBalance(balance);

        const gateways = Array.isArray(res.data.payementGateway)
          ? res.data.payementGateway
          : [];

        setPaymentGateways(gateways);

        // Normalize gateway ID to string
        if (gateways.length > 0) {
          setSelectedGatewayId(String(gateways[0].pid));
        } else {
          setSelectedGatewayId("");
        }
      } else {
        showToast(false, "Unable to fetch wallet balance");
      }
    } catch (error) {
      console.error("getWalletBalance error:", error);
      showToast(false, "Something went wrong");
    }
  };

  // =========================================================
  // TOAST
  // =========================================================
  const showToast = (success, message) => {
    Swal.fire({
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
      background: success ? "#3659cd" : "#a61c09",
      color: "white",
      title: message,
    });
  };

  // =========================================================
  // AMOUNT INPUT
  // =========================================================
  const handleAmountInput = (e) => {
    const value = e.target.value;

    // Allow empty input
    if (value === "") {
      setRechargeAmount(0);
      setSelectedQuickAmount(null);
      return;
    }

    // Convert to number immediately
    const amount = Number(value);

    if (Number.isNaN(amount)) {
      setRechargeAmount(0);
      setSelectedQuickAmount(null);
      return;
    }

    setRechargeAmount(amount);

    // If manually entered amount matches a quick amount,
    // highlight that quick amount.
    const matchingQuickAmount = QUICK_AMOUNTS.find(
      (quickAmount) => quickAmount === amount
    );

    if (matchingQuickAmount) {
      setSelectedQuickAmount(matchingQuickAmount);
    } else {
      setSelectedQuickAmount(null);
    }
  };

  // =========================================================
  // QUICK AMOUNT
  // =========================================================
  const handleChipClick = (amount) => {
    const numericAmount = Number(amount);

    setRechargeAmount(numericAmount);
    setSelectedQuickAmount(numericAmount);
  };

  // =========================================================
  // PAYMENT GATEWAY
  // =========================================================
  const handleGatewayChange = (e) => {
    setSelectedGatewayId(String(e.target.value));
  };

  // =========================================================
  // VALIDATE AMOUNT
  // =========================================================
  const validateAmount = () => {
    const amount = Number(rechargeAmount);

    if (!amount || Number.isNaN(amount)) {
      showToast(false, "Please enter recharge amount");
      return false;
    }

    if (amount < MIN_RECHARGE_AMOUNT) {
      showToast(
        false,
        `Minimum recharge amount is ₹${MIN_RECHARGE_AMOUNT.toLocaleString(
          "en-IN"
        )}`
      );
      return false;
    }

    if (amount > MAX_RECHARGE_AMOUNT) {
      showToast(
        false,
        `Maximum recharge amount is ₹${MAX_RECHARGE_AMOUNT.toLocaleString(
          "en-IN"
        )}`
      );
      return false;
    }

    return true;
  };

  // =========================================================
  // SUBMIT PAYMENT
  // =========================================================
  const submitPayment = async () => {
    if (!validateAmount()) {
      return;
    }

    if (!selectedGatewayId) {
      showToast(false, "Please select a payment method");
      return;
    }

    const amount = Number(rechargeAmount);

    setLoading(true);

    try {
      const dataToSend = {
        total_amount: amount,
        pid: selectedGatewayId,
        redirectUrl: window.location.origin + "/custom.html",
      };

      console.log("Recharge payment request:", dataToSend);

      const url = config.apiUrl + "/user/payment_phone_pay";

      const res = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      setLoading(false);

      console.log("Recharge payment response:", res.data);

      if (!res.data.status) {
        showToast(false, res.data.message || "Payment initiation failed");
        return;
      }

      const output = res.data.output;

      if (!output) {
        showToast(false, "Invalid payment gateway response");
        return;
      }

      // =====================================================
      // PHONEPE
      // =====================================================
      if (String(output.pid) === "169302936500") {
        const redirectUrl = output.instrumentResponse?.redirectInfo?.url;

        if (!redirectUrl) {
          showToast(false, "Payment redirect URL not found");
          return;
        }

        window.location.replace(redirectUrl);
        return;
      }

      // =====================================================
      // RAZORPAY
      // =====================================================
      if (String(output.pid) === "169302936502") {
        payUsingRazorpay(
          output.order_id,
          amount,
          output.transaction_id,
          output.gateway_id
        );
        return;
      }

      showToast(false, "Payment gateway not supported");
    } catch (error) {
      setLoading(false);

      console.error("Payment initiation error:", error);

      showToast(false, "Payment initiation error");
    }
  };

  // =========================================================
  // RAZORPAY PAYMENT
  // =========================================================
  const payUsingRazorpay = (orderId, amount, transactionId, gatewayId) => {
    if (!isLoaded || !Razorpay) {
      showToast(false, "Payment gateway is still loading. Please try again.");
      return;
    }

    const numericAmount = Number(amount);

    const options = {
      key: gatewayId,

      // Razorpay expects paise
      amount: Math.round(numericAmount * 100),

      currency: "INR",

      name: "ShipUncle",

      description: "Wallet Recharge",

      image: "https://app.shipuncle.in/img/logo_5.png",

      order_id: orderId,

      handler: function (response) {
        razorPaymentVerify({
          payment_response: response,
          amount: numericAmount,
          transaction_id: transactionId,
          order_id: orderId,
        });
      },

      prefill: {
        name: userData.full_name || "",
        email: userData.email || "",
        contact: userData.mobile || "",
      },

      notes: {
        address: userData.mobile || "",
      },

      theme: {
        color: "#3399cc",
      },
    };

    console.log("Razorpay options:", options);

    const rzp = new Razorpay(options);

    rzp.on("payment.failed", function (response) {
      console.error("Razorpay payment failed:", response);

      showToast(
        false,
        "Payment failed: " + (response.error?.description || "Unknown error")
      );

      setLoading(false);
    });

    rzp.open();
  };

  // =========================================================
  // RAZORPAY VERIFY
  // =========================================================
  const razorPaymentVerify = async (dataToSend) => {
    try {
      const url = config.apiUrl + "/user/payment_razor_pay_verify";

      const res = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (res.data.status) {
        showToast(true, "Wallet recharged successfully!");

        await getWalletBalance();

        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 1500);
      } else {
        showToast(false, "Verification failed. Please contact support.");
      }
    } catch (error) {
      console.error("Razorpay verification error:", error);

      showToast(false, "Verification error");
    }
  };

  // =========================================================
  // FORMAT AMOUNT
  // =========================================================
  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN");
  };

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <div>
      <Header>
        <div className="content">
          {/* =====================================================
              PAGE HEADER
          ===================================================== */}
          <div className="ol-header">
            <div className="ol-hd">
              <button
                type="button"
                className="ol-back"
                title="Go back"
                onClick={() => window.history.back()}
              >
                <i className="bi bi-arrow-left"></i>
              </button>

              <div>
                <div className="ol-title">Recharge Wallet</div>

                <div className="ol-sub">
                  Add funds to your wallet and pay for shipping as you go
                </div>
              </div>
            </div>

            <div className="ol-actions">
              <a href="/billing_passbook" className="ol-btn ghost">
                <i className="bi bi-journal-text"></i> Passbook
              </a>

              <a href="/billing_recharge" className="ol-btn">
                <i className="bi bi-clock-history"></i> Recharge History
              </a>
            </div>
          </div>

          {/* =====================================================
              MAIN WRAPPER
          ===================================================== */}
          <div className="rw-wrap">
            {/* ===================================================
                BALANCE
            =================================================== */}
            <div className="rw-balance">
              <div className="rw-balance-deco rw-balance-deco-1"></div>

              <div className="rw-balance-deco rw-balance-deco-2"></div>

              <div className="rw-balance-top">
                <span className="rw-balance-label">
                  <i className="bi bi-wallet2"></i> Current Balance
                </span>

                <span className="rw-balance-badge">
                  <i className="bi bi-arrow-repeat"></i> Auto-debit on shipping
                </span>
              </div>

              <div className="rw-balance-amt">
                ₹{Number(walletBalance || 0).toFixed(2)}
              </div>

              {/* <div className="rw-balance-meta">
                <span>
                  Wallet ID <b>SKY-WAL-0421</b>
                </span>

                <span>
                  Last Recharge <b>—</b>
                </span>
              </div> */}
            </div>

            {/* ===================================================
                RECHARGE GRID
            =================================================== */}
            <div className="rw-grid">
              {/* =================================================
                  ADD MONEY CARD
              ================================================= */}
              <div className="card-base ol-co-card rw-card">
                <div className="ol-co-head">
                  <i className="bi bi-plus-circle"></i> Add Money
                </div>

                <div className="ol-co-body rw-body">
                  {/* =================================================
                      AMOUNT
                  ================================================= */}
                  <label className="rw-label" htmlFor="rwAmount">
                    Enter amount
                  </label>

                  <div className="ol-input-affix ol-affix-left rw-input">
                    <span>₹</span>

                    <input
                      type="number"
                      id="rwAmount"
                      value={rechargeAmount === 0 ? "" : rechargeAmount}
                      onChange={handleAmountInput}
                      inputMode="numeric"
                      autoComplete="off"
                      min={MIN_RECHARGE_AMOUNT}
                      max={MAX_RECHARGE_AMOUNT}
                    />
                  </div>

                  <small className="ol-hint-tx">
                    Minimum ₹500 · Maximum ₹50,00,000
                  </small>

                  {/* =================================================
                      QUICK AMOUNTS
                  ================================================= */}
                  <div className="rw-label rw-mt">Quick amounts</div>

                  <div className="rw-chips" id="rwChips">
                    {QUICK_AMOUNTS.map((amount) => {
                      const isSelected =
                        Number(selectedQuickAmount) === Number(amount);

                      return (
                        <button
                          key={amount}
                          type="button"
                          className={`rw-chip ${isSelected ? "on" : ""}`}
                          onClick={() => handleChipClick(amount)}
                        >
                          ₹{formatAmount(amount)}
                        </button>
                      );
                    })}
                  </div>

                  {/* =================================================
                      PAYMENT METHOD
                  ================================================= */}
                  <div className="rw-label rw-mt">Pay using</div>

                  <div className="rw-pay" id="rwPay">
                    {paymentGateways.length > 0 ? (
                      paymentGateways.map((gw) => {
                        const gatewayId = String(gw.pid);

                        const isSelected = selectedGatewayId === gatewayId;

                        return (
                          <label
                            key={gatewayId}
                            className={`rw-pay-opt ${isSelected ? "on" : ""}`}
                          >
                            <input
                              type="radio"
                              name="rwPay"
                              value={gatewayId}
                              checked={isSelected}
                              onChange={handleGatewayChange}
                            />

                            <span className="rw-pay-check">
                              <i className="bi bi-check2"></i>
                            </span>

                            <img
                              src={`assets/img/${String(
                                gw.name || ""
                              ).toLowerCase()}.png`}
                              alt={gw.name}
                              className="rw-pay-logo"
                              onError={(e) => {
                                e.currentTarget.src = "assets/img/default.png";
                              }}
                            />

                            <span className="rw-pay-tag">
                              {gw.name === "Razorpay"
                                ? "UPI · Cards · NetBanking"
                                : "UPI"}
                            </span>
                          </label>
                        );
                      })
                    ) : (
                      <div className="text-muted">
                        No payment methods available.
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    FOOTER
                ================================================= */}
                <div className="ol-co-foot rw-foot">
                  <span>
                    Payable amount
                    <br />
                    <b className="rw-total" id="rwTotal">
                      ₹{formatAmount(rechargeAmount)}
                      .00
                    </b>
                  </span>

                  <button
                    type="button"
                    className="ol-btn primary"
                    id="rwPayBtn"
                    onClick={submitPayment}
                    disabled={
                      loading ||
                      !rechargeAmount ||
                      rechargeAmount < MIN_RECHARGE_AMOUNT ||
                      rechargeAmount > MAX_RECHARGE_AMOUNT
                    }
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-check"></i> Continue to
                        Payment
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* =================================================
                  SIDE INFORMATION
              ================================================= */}
              <div className="rw-side">
                {/* HOW IT WORKS */}
                <div className="card-base ol-co-card">
                  <div className="ol-co-head">
                    <i className="bi bi-lightning-charge"></i> How it works
                  </div>

                  <div className="rw-side-body">
                    <div className="rw-step">
                      <span className="rw-step-n">1</span>

                      <div>
                        <b>Choose an amount</b>

                        <span>Enter or pick a quick amount</span>
                      </div>
                    </div>

                    <div className="rw-step">
                      <span className="rw-step-n">2</span>

                      <div>
                        <b>Pay securely</b>

                        <span>UPI, cards or net banking</span>
                      </div>
                    </div>

                    <div className="rw-step">
                      <span className="rw-step-n">3</span>

                      <div>
                        <b>Ship &amp; auto-debit</b>

                        <span>Charges are deducted from balance</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECURE */}
                <div className="card-base ol-co-card">
                  <div className="ol-co-head">
                    <i className="bi bi-shield-check"></i> Secure &amp; instant
                  </div>

                  <div className="rw-side-body">
                    <div className="rw-point">
                      <i className="bi bi-shield-lock"></i>

                      <span>256-bit encrypted payments via Razorpay</span>
                    </div>

                    <div className="rw-point">
                      <i className="bi bi-clock"></i>

                      <span>Balance reflects instantly after payment</span>
                    </div>

                    <div className="rw-point">
                      <i className="bi bi-headset"></i>

                      <span>24×7 support for payment queries</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default Recharge_wallet;
